import React from "react";
import "./index.css";

import Icon from "../Icon";

/* Constants */
import { checkBoxesWithStatus } from "../../constants";

const ddmmyyyyToIso = (s) => {
    if (!s) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const a = parseInt(m[1], 10);
    const b = parseInt(m[2], 10);
    if (b > 12 && a <= 12) {
        return `${m[3]}-${m[1]}-${m[2]}`;
    }
    return `${m[3]}-${m[2]}-${m[1]}`;
};

const isoToDdmmyyyy = (s) => {
    if (!s) return "";
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

/*
 * Month/year picker helpers.
 *
 * We deliberately do NOT use <input type="month"> here: Firefox and desktop
 * Safari don't implement it and silently fall back to a plain text box, which
 * broke date entry on Work Experience / Education for those users. Two <select>
 * dropdowns behave identically in every browser and can't produce an invalid
 * value. The stored format is unchanged (MM/YYYY), so nothing downstream moves.
 */
const MONTHS = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
];

// Current year back to 1950, newest first.
const YEAR_OPTIONS = (() => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current; y >= 1950; y--) years.push(String(y));
    return years;
})();

const pad2 = (n) => (String(n).length === 1 ? `0${n}` : String(n));

// Parse whatever is stored (MM/YYYY, M/YYYY, YYYY-MM, DD/MM/YYYY, or YYYY) into
// { month, year } so the dropdowns reflect the current value on edit.
const parseMonthValue = (value) => {
    if (!value) return { month: "", year: "" };
    const str = String(value).trim();
    let m;
    if ((m = str.match(/^(\d{1,2})\/(\d{4})$/))) return { month: pad2(m[1]), year: m[2] };
    if ((m = str.match(/^(\d{4})-(\d{1,2})$/))) return { month: pad2(m[2]), year: m[1] };
    if ((m = str.match(/^\d{1,2}\/(\d{1,2})\/(\d{4})$/))) return { month: pad2(m[1]), year: m[2] };
    if ((m = str.match(/^(\d{4})$/))) return { month: "", year: m[1] };
    return { month: "", year: "" };
};

// Combine the dropdowns back into the stored format: month + year -> MM/YYYY,
// year alone -> YYYY (still parseable downstream), month alone -> "" (not a date
// on its own, but the dropdown selection is still held in local state so the
// user can add the year in either order).
const buildMonthValue = (month, year) => {
    if (month && year) return `${month}/${year}`;
    if (year) return year;
    return "";
};

/*
 * The stored value is a single MM/YYYY string, which can't represent "month
 * chosen but year not yet". So the two dropdowns keep their own local state and
 * only emit the combined value upward. Without this, picking the month first
 * emitted "" and the month dropdown snapped straight back to the placeholder -
 * dates only "worked" if you happened to choose the year first.
 */
const MonthYearPicker = ({ label, value, propName, onChangeText, profile }) => {

    const initial = parseMonthValue(value);
    const [month, setMonth] = React.useState(initial.month);
    const [year, setYear] = React.useState(initial.year);

    // Track the last value we emitted so we can tell our own echo apart from a
    // genuine external change (editing a different entry, cancel, reset) and
    // only resync the dropdowns in the latter case.
    const lastEmitted = React.useRef(value);
    React.useEffect(() => {
        if (value !== lastEmitted.current) {
            const p = parseMonthValue(value);
            setMonth(p.month);
            setYear(p.year);
            lastEmitted.current = value;
        }
    }, [value]);

    const commit = (nextMonth, nextYear) => {
        setMonth(nextMonth);
        setYear(nextYear);
        const out = buildMonthValue(nextMonth, nextYear);
        lastEmitted.current = out;
        handleOnChangeText(propName, out, onChangeText, profile, true);
    };

    // Keep a pre-existing year selectable even if it falls outside our range.
    const yearOptions = year && !YEAR_OPTIONS.includes(year)
        ? [year, ...YEAR_OPTIONS]
        : YEAR_OPTIONS;

    return (
        <div className="regular-text-input-wrapper regular-text-input-wrapper--date">
            <div className="regular-text-input-icon">
                <Icon name="calendar" size={18} />
            </div>
            <div className="regular-text-input-content">
                <div className="regular-text-input-label-container">
                    <p className="regular-text-input-label">{label}</p>
                </div>
                <div className="regular-text-input-container regular-text-input-month">
                    <div className="regular-text-input-select-wrap">
                        <select
                            className={`regular-text-input-select${month ? "" : " regular-text-input-select--empty"}`}
                            value={month}
                            onChange={(e) => commit(e.target.value, year)}
                            aria-label={`${label} month`}
                        >
                            <option value="">Month</option>
                            {MONTHS.map((m) => (
                                <option key={m.value} value={m.value}>{m.label}</option>
                            ))}
                        </select>
                        <Icon name="chevron-down" size={16} className="regular-text-input-select-chevron" />
                    </div>
                    <div className="regular-text-input-select-wrap">
                        <select
                            className={`regular-text-input-select${year ? "" : " regular-text-input-select--empty"}`}
                            value={year}
                            onChange={(e) => commit(month, e.target.value)}
                            aria-label={`${label} year`}
                        >
                            <option value="">Year</option>
                            {yearOptions.map((y) => (
                                <option key={y} value={y}>{y}</option>
                            ))}
                        </select>
                        <Icon name="chevron-down" size={16} className="regular-text-input-select-chevron" />
                    </div>
                </div>
            </div>
        </div>
    );

};

export default ({ label, placeholder, value, propName, unit, onChangeText, profile, isDate, isMonth }) => {

    if (isMonth) {
        return (
            <MonthYearPicker
                label={label}
                value={value}
                propName={propName}
                onChangeText={onChangeText}
                profile={profile}
            />
        );
    }

    if (isDate) {
        const inputValue = ddmmyyyyToIso(value);
        return (
            <div className="regular-text-input-wrapper regular-text-input-wrapper--date">
                <div className="regular-text-input-icon">
                    <Icon name="calendar" size={18} />
                </div>
                <div className="regular-text-input-content">
                    <div className="regular-text-input-label-container">
                        <p className="regular-text-input-label">{label}</p>
                    </div>
                    <div className="regular-text-input-container">
                        <input
                            value={inputValue}
                            onChange={(e) => handleOnChangeText(propName, isoToDdmmyyyy(e.target.value), onChangeText, profile)}
                            onBlur={(e) => handleOnChangeText(propName, isoToDdmmyyyy(e.target.value), onChangeText, profile, true)}
                            className="regular-text-input"
                            type="date"
                            placeholder={placeholder}
                        />
                    </div>
                </div>
            </div>
        );
    }

    return(
        <div className="regular-text-input-wrapper">
            <div className="regular-text-input-content">
                <div className="regular-text-input-label-container">
                    <p className="regular-text-input-label">{label}</p>
                </div>
                <div className="regular-text-input-container">
                    <input value={value} onChange={(e) => handleOnChangeText(propName, e.target.value, onChangeText, profile)} onBlur={(e) => handleOnChangeText(propName, e.target.value, onChangeText, profile, true)} className="regular-text-input" type="text" placeholder={placeholder} />
                    {unit &&
                        <div className="regular-text-input-unit-container">
                            <p className="regular-text-input-unit">{unit}</p>
                        </div>
                    }
                </div>
            </div>
        </div>
    );

}

const handleOnChangeText = (propName, value, onChangeText, profile, autoSave) => {

    if (checkBoxesWithStatus.includes(propName)) {
        value = { ...profile[propName], expiryDate: value };
    }

    if (propName === "reference" || propName === "startDateApprx") {
        return onChangeText(value);
    } else {
        return onChangeText([{ propName: propName, value: value }], autoSave);
    }

}
